import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { db } from "@lib/firebase";
import {
  collection,
  query,
  onSnapshot,
  where,
  limit,
  startAfter,
  orderBy,
  getDocs,
  doc,
  getDoc,
} from "firebase/firestore";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faClock,
  faCheckCircle,
  faSearch,
  faSortUp,
  faSortDown,
  faChevronDown,
} from "@fortawesome/free-solid-svg-icons";

const PAGE_SIZE = 50;

const SkeletonRow = React.memo(() => (
  <tr className="border-b border-gray-200">
    <td className="px-4 py-4">
      <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
    </td>
    <td className="px-4 py-4 hidden md:table-cell">
      <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
    </td>
    <td className="px-4 py-4">
      <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
    </td>
    <td className="px-4 py-4">
      <div className="h-4 bg-gray-200 rounded animate-pulse w-4"></div>
    </td>
  </tr>
));

const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
};

export const EntryList = React.memo(() => {
  const { eventId, category } = useParams();

  const [state, setState] = useState({
    participants: [],
    allParticipants: [],
    filteredParticipants: [],
    fullFilteredList: [],
    searchTerm: "",
    eventTitle: "",
    eventType: "",
    collectionName: "",
    // categories removed from state to prevent re-render loops affecting logic functions
    loading: true,
    loadingMore: false,
    error: null,
    lastVisible: null,
    totalParticipants: 0,
    totalFiltered: 0,
    sortGenderDirection: null,
    hasMore: true,
    searchPageIndex: 0,
  });
  const isMounted = useRef(true);
  const unsubscribeRef = useRef({
    participants: null,
    total: null,
  });
  const searchPageIndexRef = useRef(0);

  const debouncedSearchTerm = useDebounce(state.searchTerm, 300);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
      Object.values(unsubscribeRef.current).forEach((unsubscribe) => {
        if (unsubscribe) unsubscribe();
      });
    };
  }, []);

  const categoriesRef = useRef([]);

  const mapCategoryToFirestore = useCallback(
    (distance, categoriesOverride = null) => {
      if (!distance || distance === "all") {
        return null;
      }
      
      // Use override if provided, otherwise fallback to ref (stable) - NOT state
      const cats = categoriesOverride || categoriesRef.current;
      const hit = cats.find(
        (c) => c.distance.toString() === distance.toString()
      );

      if (hit) return hit.categoryName;

      // Fallback for legacy hardcoded values if categories are empty
      if (cats.length === 0) {
        switch (distance) {
          case "7":
            return "Fun Trail (7K)";
          case "7-fun":
            return "Fun Trail (7K)";
          case "20":
            return ["Classic Distance (20K)", "Mix Group (20K)"];
          case "40":
            return "Short Distance (40K)";
          case "80":
            return "Long Distance (80K)";
        }
      }

      throw new Error(`Invalid category: ${distance}`);
    },
    [] 
    // Removed state.categories dependency to prevent loop. 
    // categoriesRef is stable.
  );

  const fetchEventData = useCallback(async () => {
    try {
      const eventRef = doc(db, "semuaKegiatan", eventId);
      const eventDoc = await getDoc(eventRef);
      if (eventDoc.exists()) {
        const eventData = eventDoc.data();
        const collectionName =
          (await getDoc(doc(db, "events", eventData.eventId))).data()
            ?.collectionName || "pendaftaranJOTR";
        const categories = eventData.categories || [];
        categoriesRef.current = categories; // Update ref immediately

        if (isMounted.current) {
          setState((prev) => ({
            ...prev,
            eventTitle: eventData.title || eventId,
            eventType: eventData.type || "",
            collectionName,
            loading: true,
          }));
          // Validate category
          if (category && category !== "all") {
            try {
              mapCategoryToFirestore(category, categories);
            } catch (err) {
              throw new Error(`Invalid category: ${category}`);
            }
          }
          // Fetch initial data
          fetchAllParticipants(collectionName, category, categories);
          loadPage(collectionName, null, null, category, categories);
        }
      } else {
        throw new Error(`Dokumen ${eventId} tidak ditemukan di semuaKegiatan`);
      }
    } catch (err) {
      if (isMounted.current) {
        setState((prev) => ({...prev, error: `Gagal memuat data kegiatan: ${err.message}`, loading: false }));
      }
    }
  }, [eventId, category, mapCategoryToFirestore]);

  const loadPage = useCallback(
    (
      collectionName,
      lastDoc,
      sortDirection,
      selectedDistance,
      categoriesOverride = null
    ) => {
      setState((prev) => ({
        ...prev,
        loading: !lastDoc,
        loadingMore: !!lastDoc,
        participants: lastDoc ? prev.participants : [],
        filteredParticipants: debouncedSearchTerm
          ? prev.filteredParticipants
          : [],
      }));

      const participantsCollection = collection(db, collectionName);
      const baseConstraints = [
        // where("transaction_status", "in", ["settlement", "pending"]), // Temporarily disabled for debugging
      ];
      const firestoreCategory = mapCategoryToFirestore(
        selectedDistance,
        categoriesOverride
      );
      if (firestoreCategory) {
        if (Array.isArray(firestoreCategory)) {
          baseConstraints.push(where("kategori", "in", firestoreCategory));
        } else {
          baseConstraints.push(where("kategori", "==", firestoreCategory));
        }
      }
      const orderConstraints = [];
      if (sortDirection) {
        orderConstraints.push(orderBy("gender", sortDirection));
      }
      orderConstraints.push(orderBy("transaction_status"));
      orderConstraints.push(orderBy("namaLengkap"));

      const qParticipants = lastDoc
        ? query(
            participantsCollection,
            ...baseConstraints,
            ...orderConstraints,
            startAfter(lastDoc),
            limit(PAGE_SIZE)
          )
        : query(
            participantsCollection,
            ...baseConstraints,
            ...orderConstraints,
            limit(PAGE_SIZE)
          );

      const unsubscribe = onSnapshot(
        qParticipants,
        (snapshot) => {
          if (!isMounted.current) return;

          if (snapshot.empty) {
            setState((prev) => ({
              ...prev,
              participants: debouncedSearchTerm ? prev.participants : [],
              filteredParticipants: debouncedSearchTerm
                ? prev.filteredParticipants
                : [],
              lastVisible: null,
              loading: false,
              loadingMore: false,
              hasMore: false,
            }));
            return;
          }

          const participantList = snapshot.docs.map((doc) => {
            const data = doc.data();
            return {
              id: doc.id,
              nama:
                data.teamName && data.kategori === "Mix Group (20K)"
                  ? data.teamName
                  : data.namaLengkap || "Name not available",
              gender: data.gender || "Unknown",
              kota: data.kota || data.alamat || "Unknown",
              statusPembayaran: data.transaction_status || "pending",
              category: data.kategori || "Unknown",
              docRef: doc,
            };
          });

          setState((prev) => {
            const updatedParticipants = lastDoc
              ? [...prev.participants, ...participantList]
              : participantList;
            return {
              ...prev,
              participants: updatedParticipants,
              filteredParticipants: debouncedSearchTerm
                ? prev.filteredParticipants
                : updatedParticipants,
              lastVisible:
                snapshot.docs.length > 0
                  ? snapshot.docs[snapshot.docs.length - 1]
                  : null,
              loading: false,
              loadingMore: false,
              totalFiltered: debouncedSearchTerm
                ? prev.totalFiltered
                : prev.totalParticipants,
              hasMore: snapshot.size === PAGE_SIZE,
            };
          });
        },
        (err) => {
          if (!isMounted.current) return;
          setState((prev) => ({
            ...prev,
            error: `Failed to load participant data: ${err.message}. Please check Firestore index or contact admin.`,
            loading: false,
            loadingMore: false,
          }));
        }
      );

      unsubscribeRef.current.participants = unsubscribe;
    },
    [debouncedSearchTerm, mapCategoryToFirestore]
  );

  const fetchAllParticipants = useCallback(
    async (collectionName, selectedDistance, categoriesOverride = null) => {
      try {
        const participantsCollection = collection(db, collectionName);
        const baseConstraints = [
          // where("transaction_status", "in", ["settlement", "pending"]), // Temporarily disabled
        ];
        const firestoreCategory = mapCategoryToFirestore(
          selectedDistance,
          categoriesOverride
        );
        if (firestoreCategory) {
          if (Array.isArray(firestoreCategory)) {
            baseConstraints.push(where("kategori", "in", firestoreCategory));
          } else {
            baseConstraints.push(where("kategori", "==", firestoreCategory));
          }
        }
        const q = query(participantsCollection, ...baseConstraints);

        const snapshot = await getDocs(q);
        const allParticipants = snapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            nama:
              data.teamName && data.kategori === "Mix Group (20K)"
                ? data.teamName
                : data.namaLengkap || "Name not available",
            gender: data.gender || "Unknown",
            kota: data.kota || data.alamat || "Unknown",
            statusPembayaran: data.transaction_status || "pending",
            category: data.kategori || "Unknown",
            docRef: doc,
          };
        });
        if (isMounted.current) {
          setState((prev) => ({
            ...prev,
            allParticipants,
            totalParticipants: snapshot.size,
            totalFiltered: debouncedSearchTerm
              ? prev.totalFiltered
              : snapshot.size,
          }));
        }
      } catch (err) {
        if (isMounted.current) {
          setState((prev) => ({
            ...prev,
            error: `Failed to load all participants: ${err.message}. Please contact admin.`,
            loading: false,
          }));
        }
      }
    },
    [debouncedSearchTerm, mapCategoryToFirestore]
  );

  useEffect(() => {
    fetchEventData();
  }, [fetchEventData]);

  useEffect(() => {
    if (!state.collectionName) return;
    const participantsCollection = collection(db, state.collectionName);
    const baseConstraints = [
      // where("transaction_status", "in", ["settlement", "pending"]), // Temporarily disabled
    ];
    const firestoreCategory = mapCategoryToFirestore(category);
    if (firestoreCategory) {
      if (Array.isArray(firestoreCategory)) {
        baseConstraints.push(where("kategori", "in", firestoreCategory));
      } else {
        baseConstraints.push(where("kategori", "==", firestoreCategory));
      }
    }
    const q = query(participantsCollection, ...baseConstraints);
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        if (!isMounted.current) return;
        setState((prev) => ({
          ...prev,
          totalParticipants: snapshot.size,
          totalFiltered: debouncedSearchTerm
            ? prev.totalFiltered
            : snapshot.size,
        }));
      },
      (err) => {
        if (!isMounted.current) return;
        setState((prev) => ({
          ...prev,
          error: `Failed to load total participants: ${err.message}`,
        }));
      }
    );
    unsubscribeRef.current.total = unsubscribe;
    return () => unsubscribe();
  }, [
    state.collectionName,
    debouncedSearchTerm,
    category,
    mapCategoryToFirestore,
  ]);

  useEffect(() => {
    if (!state.collectionName) return;

    if (debouncedSearchTerm) {
      const filteredList = state.allParticipants.filter((p) => {
        const nama = p.nama || "";
        return nama
          .toLowerCase()
          .includes(debouncedSearchTerm.toLowerCase().trim());
      });

      setState((prev) => {
        const initialParticipants = filteredList.slice(0, PAGE_SIZE);
        if (
          JSON.stringify(prev.filteredParticipants) ===
            JSON.stringify(initialParticipants) &&
          prev.totalFiltered === filteredList.length &&
          prev.fullFilteredList.length === filteredList.length
        ) {
          return prev;
        }
        searchPageIndexRef.current = 0;
        return {
          ...prev,
          filteredParticipants: initialParticipants,
          fullFilteredList: filteredList,
          totalFiltered: filteredList.length,
          hasMore: filteredList.length > PAGE_SIZE,
          lastVisible: null,
          searchPageIndex: 0,
          loading: false,
        };
      });
    } else {
      setState((prev) => {
        const newParticipants =
          prev.participants.length > 0 ? prev.participants : [];
        const newLastVisible =
          prev.participants.length > 0 &&
          prev.participants[prev.participants.length - 1]?.docRef
            ? prev.participants[prev.participants.length - 1].docRef
            : null;
        if (
          JSON.stringify(prev.filteredParticipants) ===
            JSON.stringify(newParticipants) &&
          prev.totalFiltered === prev.totalParticipants &&
          prev.lastVisible === newLastVisible
        ) {
          return prev;
        }
        searchPageIndexRef.current = 0;
        return {
          ...prev,
          filteredParticipants: newParticipants,
          fullFilteredList: [],
          totalFiltered: prev.totalParticipants,
          hasMore: newParticipants.length < prev.totalParticipants,
          lastVisible: newLastVisible,
          searchPageIndex: 0,
          loading: false,
        };
      });
    }
  }, [
    debouncedSearchTerm,
    state.allParticipants,
    state.collectionName,
    state.participants,
    state.totalParticipants,
    category,
  ]);

  const loadMore = useCallback(() => {
    if (debouncedSearchTerm) {
      if (!state.fullFilteredList.length) return;
      setState((prev) => {
        const currentIndex = searchPageIndexRef.current + 1;
        const startIndex = currentIndex * PAGE_SIZE;
        const additionalList = prev.fullFilteredList.slice(
          startIndex,
          startIndex + PAGE_SIZE
        );
        if (!additionalList.length) return { ...prev, hasMore: false };
        searchPageIndexRef.current = currentIndex;
        return {
          ...prev,
          filteredParticipants: [
            ...prev.filteredParticipants,
            ...additionalList,
          ],
          hasMore: startIndex + PAGE_SIZE < prev.fullFilteredList.length,
          searchPageIndex: currentIndex,
          lastVisible: null,
        };
      });
    } else {
      if (state.loading || state.loadingMore || !state.hasMore) return;
      let nextLastVisible = state.lastVisible;
      if (!nextLastVisible && state.participants.length > 0) {
        nextLastVisible =
          state.participants[state.participants.length - 1]?.docRef || null;
      }
      if (!nextLastVisible) return;
      loadPage(
        state.collectionName,
        nextLastVisible,
        state.sortGenderDirection,
        category
      );
    }
  }, [
    debouncedSearchTerm,
    state.fullFilteredList,
    state.lastVisible,
    state.loading,
    state.loadingMore,
    state.collectionName,
    state.sortGenderDirection,
    state.hasMore,
    state.participants,
    category,
    loadPage,
  ]);

  const handleSearchChange = useCallback((e) => {
    const searchValue = e.target.value || "";
    setState((prev) => {
      searchPageIndexRef.current = 0;
      return {
        ...prev,
        searchTerm: searchValue,
        lastVisible: searchValue ? null : prev.lastVisible,
        hasMore: true,
        searchPageIndex: 0,
      };
    });
  }, []);

  const handleGenderSortToggle = useCallback(() => {
    const newDirection = state.sortGenderDirection === "desc" ? "asc" : "desc";
    setState((prev) => {
      searchPageIndexRef.current = 0;
      return {
        ...prev,
        sortGenderDirection: newDirection,
        lastVisible: null,
        hasMore: true,
        searchPageIndex: 0,
        loading: true,
      };
    });
    loadPage(state.collectionName, null, newDirection, category);
  }, [state.sortGenderDirection, state.collectionName, loadPage, category]);

  const rangeText = `${state.filteredParticipants.length} of ${
    debouncedSearchTerm ? state.totalFiltered : state.totalParticipants
  } Participants`;

  const getCategoryLabel = (cat) => {
    if (cat === "all") {
      return "All Categories";
    }
    return `${cat} KM`;
  };

  if (state.error) {
    return (
      <section className="w-full min-h-screen flex items-center justify-center flex-col bg-white text-center px-4">
        <p className="text-lg text-gray-600 mb-4">{state.error}</p>
        <Link to="/kegiatan" className="text-cyan-600 underline text-base">
          Back to Event List
        </Link>
      </section>
    );
  }

  return (
    <section className="w-full min-h-screen py-20 text-center text-cyan-600 flex-col justify-center relative bg-white">
      <div className="relative z-1 container mx-auto px-4">
        <motion.div
          className="mb-6 flex flex-col gap-4 max-w-6xl mx-auto"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: "easeInOut" }}
          viewport={{ once: true }}
        >
          {/* Mobile title */}
          <h3 className="text-lg md:text-xl font-black uppercase text-cyan-600 text-center block md:hidden">
            {state.eventType === "seminar"
              ? state.eventTitle
              : `${state.eventTitle} - ${getCategoryLabel(category)}`}
          </h3>

          {/* Search + Desktop title in one row */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            {/* Search */}
            <div className="relative w-full max-w-md">
              <input
                type="text"
                placeholder="Search by name..."
                value={state.searchTerm}
                onChange={handleSearchChange}
                className="input w-full p-2 pl-10 border border-gray-300 rounded-xl focus:ring-2 focus:ring-cyan-600 text-slate-800 text-sm"
                aria-label="Search participants by name"
              />
              <FontAwesomeIcon
                icon={faSearch}
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
              />
            </div>

            {/* Desktop title */}
            <h3 className="text-lg md:text-xl font-black uppercase text-cyan-600 md:text-right hidden md:block">
              {state.eventType === "seminar"
                ? state.eventTitle
                : `${state.eventTitle} - ${getCategoryLabel(category)}`}
            </h3>
          </div>
        </motion.div>

        <motion.div
          className="bg-white rounded-2xl border border-gray-200 text-left relative overflow-hidden max-w-6xl mx-auto"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: "easeInOut" }}
          viewport={{ once: true }}
        >
          <div className="overflow-x-auto">
            <table className="w-full table-auto divide-y divide-gray-200">
              <thead>
                <tr className="bg-gray-100">
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                    Name
                  </th>
                  <th
                    className="px-4 py-3 text-left md:text-center text-xs font-medium text-gray-600 uppercase tracking-wider cursor-pointer hover:text-cyan-600 hidden md:table-cell"
                    onClick={handleGenderSortToggle}
                    aria-label="Sort by gender"
                  >
                    <span className="flex items-center md:justify-center">
                      Gender
                      {state.sortGenderDirection && (
                        <FontAwesomeIcon
                          icon={
                            state.sortGenderDirection === "asc"
                              ? faSortUp
                              : faSortDown
                          }
                          className="ml-1 text-gray-500"
                        />
                      )}
                    </span>
                  </th>
                  <th className="px-4 py-3 text-left md:text-center text-xs font-medium text-gray-600 uppercase tracking-wider">
                    City
                  </th>
                  <th className="px-4 py-3 text-left md:text-center text-xs font-medium text-gray-600 uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody>
                {state.loading ? (
                  Array.from({ length: 5 }).map((_, index) => (
                    <SkeletonRow key={index} />
                  ))
                ) : state.filteredParticipants.length === 0 ? (
                  <tr>
                    <td
                      colSpan={4}
                      className="px-4 py-4 text-center text-sm text-gray-600"
                    >
                      {state.searchTerm
                        ? "No participants match your search."
                        : "No participants registered yet."}
                    </td>
                  </tr>
                ) : (
                  state.filteredParticipants.map((participant) => (
                    <tr
                      key={participant.id}
                      className="border-b border-gray-200"
                    >
                      <td
                        className={`px-4 py-4 text-sm whitespace-nowrap md:whitespace-normal ${
                          participant.gender === "Perempuan"
                            ? "text-cyan-600"
                            : "text-gray-600"
                        }`}
                      >
                        {participant.nama
                          ? participant.nama.toUpperCase()
                          : "-"}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-600 hidden md:table-cell md:text-center">
                        {participant.gender || "-"}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-600 md:text-center">
                        {participant.kota || "-"}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-600">
                        {participant.statusPembayaran === "settlement" ? (
                          <FontAwesomeIcon
                            icon={faCheckCircle}
                            className="text-cyan-600 mx-auto block"
                            title="Settled"
                          />
                        ) : (
                          <FontAwesomeIcon
                            icon={faClock}
                            className="text-yellow-500 mx-auto block"
                            title="Pending"
                          />
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <div className="flex justify-end items-center px-4 py-2">
            <span className="text-sm text-gray-600 mr-4">{rangeText}</span>
            <button
              onClick={loadMore}
              disabled={state.loading || state.loadingMore || !state.hasMore}
              className={`px-3 py-1 rounded-lg transition-colors duration-200 flex items-center gap-1 ${
                state.loading || state.loadingMore || !state.hasMore
                  ? "bg-gray-200 text-gray-600 cursor-not-allowed"
                  : "bg-cyan-600 text-white hover:bg-cyan-700"
              }`}
              aria-label={
                state.hasMore
                  ? "Load more participants"
                  : "End of participant list"
              }
            >
              <span>{state.hasMore ? "See more" : "End of list"}</span>
              <FontAwesomeIcon icon={faChevronDown} />
            </button>
          </div>
        </motion.div>
      </div>
    </section>
  );
});
